/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import * as React from 'react';
import styled, { StyledComponent } from '@emotion/styled';
import type { Property } from 'csstype';
import applyNesting from './apply-nesting';

type Props = {
  onPress?: (event: React.SyntheticEvent) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode | React.ReactNodeArray;
  inline?: boolean;
  accessible?: boolean;
};

function BaseView({
  onPress,
  disabled,
  className,
  inline,
  children,
  accessible: _accessible, // react-native property only
  ...rest
}: Props & React.HTMLAttributes<HTMLDivElement>) {
  const handleClick = React.useCallback(
    (event) => {
      if (onPress && !disabled) {
        return onPress(event);
      }
      return null;
    },
    [onPress, disabled],
  );

  return (
    <div onClick={handleClick} className={className} {...rest}>
      {inline ? applyNesting(children) : children}
    </div>
  );
}

type StyledViewProps = {
  alignItems?: string;
  inline?: boolean;
  flexDirection?: Property.FlexDirection;
};
const View: StyledComponent<
  StyledViewProps & React.ComponentProps<typeof BaseView>
> = styled(BaseView)<StyledViewProps>((props) => ({
  alignItems: props.alignItems ?? 'stretch',
  borderWidth: 0,
  borderStyle: 'solid',
  boxSizing: 'border-box',
  display: props.inline ? 'inline-flex' : 'flex',
  flexDirection: props.flexDirection ?? 'column',
  flexShrink: 0,
  margin: 0,
  padding: 0,
  position: 'relative',
  zIndex: 0,
  // fix flexbox bugs
  minHeight: 0,
  minWidth: 0,
}));

export default View;
